/**
 * PartnerHomeView
 *
 * Partner homepage variant. Centers on the partner's own tasks
 * and shows the primary partner's journey progress read-only.
 */

import React, { useState } from "react";
import { View, Text, Pressable } from "react-native";
import {
  Users,
  Check,
  ChevronDown,
  ChevronUp,
  Heart,
  Circle,
  CheckCircle2,
} from "lucide-react-native";
import { EMBER } from "@louise/moon-ui/theme/tokens";
import { useTranslations, type TranslationFunction } from "@src/packages/i18n";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";
import type { PlanAction, Substep, JourneyDefinition } from "../plan.types";
import type { StepProgress } from "../hooks/usePlanProgress";

type Props = {
  partnerName?: string;
  primaryName?: string;
  journey: JourneyDefinition;
  progress: StepProgress[];
  activeStepIndex: number;
  /** The partner's own tasks extracted from partner tracks */
  partnerTasks: Substep[];
  /** What treatment phase the primary is in (for support cards) */
  primaryPhase?: string;
  onAction?: (action: PlanAction) => void;
};

type SupportCard = {
  title: string;
  physicalContext: string;
  emotionalContext: string;
  practicalHelp: string[];
};

/**
 * Safe boundary while the server-side consent-scoped summary/task projection
 * is unavailable. Partner relationship state alone must never relabel the
 * viewer's own journey as someone else's.
 */
export function PartnerHomeUnavailableView() {
  const t = useTranslations();

  return (
    <View
      testID="partner-home-unavailable"
      style={{
        gap: 10,
        padding: 4,
      }}
    >
      <Users size={22} color={EMBER.colors.teal} />
      <Text
        className="font-dm-sans"
        style={{
          fontSize: 17,
          lineHeight: 23,
          color: EMBER.colors.text,
          fontWeight: "600",
        }}
      >
        {t("homepage.partner_home.shared_summary_unavailable_title")}
      </Text>
      <Text
        className="font-dm-sans"
        style={{
          fontSize: 13,
          lineHeight: 20,
          color: EMBER.colors.textMid,
        }}
      >
        {t("homepage.partner_home.shared_summary_unavailable_body")}
      </Text>
      <View
        style={{
          marginTop: 6,
          paddingTop: 12,
          borderTopWidth: 1,
          borderTopColor: EMBER.colors.border,
        }}
      >
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 13,
            lineHeight: 20,
            color: EMBER.colors.textMid,
          }}
        >
          {t("homepage.partner_home.partner_tasks_unavailable_body")}
        </Text>
      </View>
    </View>
  );
}

function getSupportCard(
  stepId: string | undefined,
  primaryName: string,
  t: TranslationFunction,
): SupportCard | null {
  const titleParams = { params: { primaryName } };
  const supportTitle = t("homepage.partner_home.how_to_support", titleParams);
  switch (stepId) {
    case "test":
      return {
        title: supportTitle,
        physicalContext: t("homepage.partner_home.test_physical", titleParams),
        emotionalContext: t("homepage.partner_home.test_emotional"),
        practicalHelp: [
          t("homepage.partner_home.test_help_1"),
          t("homepage.partner_home.test_help_2"),
          t("homepage.partner_home.test_help_3"),
        ],
      };
    case "prep":
      return {
        title: supportTitle,
        physicalContext: t("homepage.partner_home.prep_physical", titleParams),
        emotionalContext: t("homepage.partner_home.prep_emotional"),
        practicalHelp: [
          t("homepage.partner_home.prep_help_1"),
          t("homepage.partner_home.prep_help_2"),
          t("homepage.partner_home.prep_help_3"),
        ],
      };
    case "treatment":
      return {
        title: supportTitle,
        physicalContext: t(
          "homepage.partner_home.treatment_physical",
          titleParams,
        ),
        emotionalContext: t("homepage.partner_home.treatment_emotional"),
        practicalHelp: [
          t("homepage.partner_home.treatment_help_1"),
          t("homepage.partner_home.treatment_help_2"),
          t("homepage.partner_home.treatment_help_3"),
          t("homepage.partner_home.treatment_help_4"),
        ],
      };
    case "wait":
      return {
        title: supportTitle,
        physicalContext: t("homepage.partner_home.wait_physical", titleParams),
        emotionalContext: t("homepage.partner_home.wait_emotional"),
        practicalHelp: [
          t("homepage.partner_home.wait_help_1"),
          t("homepage.partner_home.wait_help_2"),
          t("homepage.partner_home.wait_help_3"),
          t("homepage.partner_home.wait_help_4"),
        ],
      };
    default:
      return null;
  }
}

function PartnerTaskCard({
  task,
  onAction,
}: {
  task: Substep;
  onAction?: (action: PlanAction) => void;
}) {
  const isDone = task.status === "complete";

  return (
    <Pressable
      style={{
        flexDirection: "row",
        gap: 10,
        paddingVertical: 12,
        opacity: isDone ? 0.5 : 1,
      }}
    >
      {isDone ? (
        <CheckCircle2 size={20} color={EMBER.colors.green} />
      ) : (
        <Circle size={20} color={EMBER.colors.border} />
      )}
      <View style={{ flex: 1 }}>
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 14,
            color: EMBER.colors.text,
            fontWeight: "600",
            textDecorationLine: isDone ? "line-through" : "none",
          }}
        >
          {task.title}
        </Text>
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 12,
            lineHeight: 18,
            color: EMBER.colors.textMid,
            fontWeight: "400",
            marginTop: 2,
          }}
        >
          {task.education}
        </Text>
        {task.actions && task.actions.length > 0 && !isDone ? (
          <View
            style={{
              flexDirection: "row",
              gap: 8,
              marginTop: 8,
              flexWrap: "wrap",
            }}
          >
            {task.actions.map((action) => (
              <Pressable
                key={action.label}
                onPress={() => onAction?.(action)}
                style={{
                  paddingHorizontal: 12,
                  paddingVertical: 6,
                  borderRadius: 8,
                  backgroundColor:
                    action.variant === "primary"
                      ? EMBER.colors.warm
                      : "rgba(184, 74, 22, 0.08)",
                }}
              >
                <Text
                  className="font-dm-sans"
                  style={{
                    fontSize: 12,
                    color:
                      action.variant === "primary"
                        ? "#FFFFFF"
                        : EMBER.colors.warm,
                    fontWeight: "600",
                  }}
                >
                  {action.label}
                </Text>
              </Pressable>
            ))}
          </View>
        ) : null}
      </View>
    </Pressable>
  );
}

export default function PartnerHomeView({
  primaryName,
  journey,
  progress,
  activeStepIndex,
  partnerTasks,
  onAction,
}: Props) {
  const t = useTranslations();
  const capturePress = usePressCapture("PartnerHomeView");
  const [showSupport, setShowSupport] = useState(true);
  const resolvedPrimaryName =
    primaryName ?? t("homepage.partner_home.your_partner");
  const activeStep = journey.steps[activeStepIndex];
  const supportCard = getSupportCard(activeStep?.id, resolvedPrimaryName, t);

  const remainingTasks = partnerTasks.filter((t) => t.status !== "complete");
  const completedTasks = partnerTasks.filter((t) => t.status === "complete");

  return (
    <View style={{ gap: 16 }}>
      {/* Primary's journey progress (read-only) */}
      <View
        style={{
          backgroundColor: "rgba(255, 255, 255, 0.3)",
          borderRadius: 16,
          padding: 16,
          borderWidth: 1,
          borderColor: "rgba(255, 255, 255, 0.4)",
        }}
      >
        <View
          style={{
            flexDirection: "row",
            alignItems: "center",
            gap: 8,
            marginBottom: 12,
          }}
        >
          <Users size={16} color={EMBER.colors.teal} />
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 11,
              color: EMBER.colors.textLight,
              fontWeight: "600",
              letterSpacing: 0.5,
              textTransform: "uppercase",
            }}
          >
            {t("homepage.partner_home.primary_journey", {
              params: {
                primaryName: resolvedPrimaryName,
                journeyTitle: journey.title,
              },
            })}
          </Text>
        </View>

        {/* Mini stepper */}
        <View style={{ flexDirection: "row", alignItems: "center", gap: 4 }}>
          {journey.steps.map((step, idx) => {
            const stepProgress = progress[idx];
            const isComplete = stepProgress?.status === "complete";
            const isActive = idx === activeStepIndex;

            return (
              <View
                key={step.id}
                style={{ flex: 1, alignItems: "center", gap: 4 }}
              >
                <View
                  style={{
                    height: 4,
                    width: "100%",
                    borderRadius: 2,
                    backgroundColor: isComplete
                      ? EMBER.colors.green
                      : isActive
                        ? EMBER.colors.warm
                        : "rgba(42, 37, 34, 0.08)",
                  }}
                />
                <Text
                  className="font-dm-sans"
                  style={{
                    fontSize: 9,
                    color: isActive
                      ? EMBER.colors.warm
                      : EMBER.colors.textFaint,
                    fontWeight: isActive ? "600" : "400",
                    textAlign: "center",
                  }}
                  numberOfLines={1}
                >
                  {step.title.split(" ")[0]}
                </Text>
              </View>
            );
          })}
        </View>
      </View>

      {/* Partner's tasks */}
      <View
        style={{
          backgroundColor: "rgba(255, 255, 255, 0.4)",
          borderRadius: 20,
          borderWidth: 1,
          borderColor: "rgba(184, 74, 22, 0.15)",
          padding: 16,
        }}
      >
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 11,
            color: EMBER.colors.textLight,
            fontWeight: "600",
            letterSpacing: 0.5,
            textTransform: "uppercase",
            marginBottom: 4,
          }}
        >
          {t("homepage.partner_home.your_tasks")}
        </Text>
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 13,
            color: EMBER.colors.textMid,
            fontWeight: "400",
            marginBottom: 12,
          }}
        >
          {remainingTasks.length > 0
            ? t("homepage.partner_home.count_remaining", {
                params: { count: String(remainingTasks.length) },
              })
            : t("homepage.partner_home.all_done")}
        </Text>

        {remainingTasks.map((task, idx) => (
          <View key={task.id}>
            {idx > 0 ? (
              <View
                style={{
                  height: 1,
                  backgroundColor: EMBER.colors.border,
                  opacity: 0.3,
                }}
              />
            ) : null}
            <PartnerTaskCard task={task} onAction={onAction} />
          </View>
        ))}

        {completedTasks.length > 0 ? (
          <View style={{ marginTop: 8 }}>
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 11,
                color: EMBER.colors.textFaint,
                fontWeight: "500",
                marginBottom: 4,
              }}
            >
              {t("homepage.partner_home.completed_count", {
                params: { count: String(completedTasks.length) },
              })}
            </Text>
            {completedTasks.map((task) => (
              <View
                key={task.id}
                style={{
                  flexDirection: "row",
                  alignItems: "center",
                  gap: 8,
                  paddingVertical: 4,
                }}
              >
                <Check size={14} color={EMBER.colors.green} />
                <Text
                  className="font-dm-sans"
                  style={{
                    fontSize: 12,
                    color: EMBER.colors.textLight,
                    fontWeight: "400",
                    textDecorationLine: "line-through",
                  }}
                >
                  {task.title}
                </Text>
              </View>
            ))}
          </View>
        ) : null}
      </View>

      {/* How to support card */}
      {supportCard ? (
        <View
          style={{
            backgroundColor: "rgba(253, 218, 176, 0.12)",
            borderRadius: 16,
            borderWidth: 1,
            borderColor: "rgba(253, 218, 176, 0.25)",
            overflow: "hidden",
          }}
        >
          <Pressable
            onPress={() => {
              capturePress(
                pressLabel(supportCard.title),
                showSupport
                  ? "partner_support_card_collapse"
                  : "partner_support_card_expand",
              );
              setShowSupport(!showSupport);
            }}
            style={{
              flexDirection: "row",
              alignItems: "center",
              padding: 16,
              gap: 8,
            }}
          >
            <Heart size={16} color={EMBER.colors.coral} />
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 14,
                color: EMBER.colors.text,
                fontWeight: "600",
                flex: 1,
              }}
            >
              {supportCard.title}
            </Text>
            {showSupport ? (
              <ChevronUp size={16} color={EMBER.colors.textLight} />
            ) : (
              <ChevronDown size={16} color={EMBER.colors.textLight} />
            )}
          </Pressable>

          {showSupport ? (
            <View style={{ paddingHorizontal: 16, paddingBottom: 16, gap: 12 }}>
              <View>
                <Text
                  className="font-dm-sans"
                  style={{
                    fontSize: 11,
                    color: EMBER.colors.textLight,
                    fontWeight: "600",
                    letterSpacing: 0.3,
                    textTransform: "uppercase",
                    marginBottom: 4,
                  }}
                >
                  {t("homepage.partner_home.what_experiencing")}
                </Text>
                <Text
                  className="font-dm-sans"
                  style={{
                    fontSize: 13,
                    lineHeight: 19,
                    color: EMBER.colors.textMid,
                    fontWeight: "400",
                  }}
                >
                  {supportCard.physicalContext}
                </Text>
              </View>

              <View>
                <Text
                  className="font-dm-sans"
                  style={{
                    fontSize: 11,
                    color: EMBER.colors.textLight,
                    fontWeight: "600",
                    letterSpacing: 0.3,
                    textTransform: "uppercase",
                    marginBottom: 4,
                  }}
                >
                  {t("homepage.partner_home.how_feeling")}
                </Text>
                <Text
                  className="font-dm-sans"
                  style={{
                    fontSize: 13,
                    lineHeight: 19,
                    color: EMBER.colors.textMid,
                    fontWeight: "400",
                  }}
                >
                  {supportCard.emotionalContext}
                </Text>
              </View>

              <View>
                <Text
                  className="font-dm-sans"
                  style={{
                    fontSize: 11,
                    color: EMBER.colors.textLight,
                    fontWeight: "600",
                    letterSpacing: 0.3,
                    textTransform: "uppercase",
                    marginBottom: 4,
                  }}
                >
                  {t("homepage.partner_home.how_you_help")}
                </Text>
                {supportCard.practicalHelp.map((help) => (
                  <View
                    key={help}
                    style={{
                      flexDirection: "row",
                      gap: 8,
                      paddingVertical: 3,
                    }}
                  >
                    <Text
                      className="font-dm-sans"
                      style={{
                        fontSize: 13,
                        color: EMBER.colors.teal,
                        fontWeight: "500",
                      }}
                    >
                      •
                    </Text>
                    <Text
                      className="font-dm-sans"
                      style={{
                        fontSize: 13,
                        lineHeight: 19,
                        color: EMBER.colors.text,
                        fontWeight: "400",
                        flex: 1,
                      }}
                    >
                      {help}
                    </Text>
                  </View>
                ))}
              </View>
            </View>
          ) : null}
        </View>
      ) : null}
    </View>
  );
}
